import React, { useState, useRef, useEffect } from "react";
import CommentForm from "components/util/helpers/CommentForm";

const MIN_SIZE = 4;
const DEFAULT_POPUP_POSITION = { left: 10, top: 10 };
const EMPTY_IMAGE_LAYOUT = { left: 0, top: 0, width: 0, height: 0 };

const ImageAnnotator = ({
  src,
  annotations = [],
  annotationNumbers = {},
  onChange,
  isEditable = true,
  alt = "Image",
  currentAuthor = "",
  defaultAnnotationData = {}
}) => {

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const [drawing, setDrawing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [imageLayout, setImageLayout] = useState(EMPTY_IMAGE_LAYOUT);
  const isLayoutReady = imageLayout.width > 0 && imageLayout.height > 0;

  const updateImageLayout = () => {

    if (!containerRef.current || !imageRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const imageRect = imageRef.current.getBoundingClientRect();

    if (imageRect.width <= 0 || imageRect.height <= 0) {
      return;
    }

    setImageLayout({
      left: imageRect.left - containerRect.left,
      top: imageRect.top - containerRect.top,
      width: imageRect.width,
      height: imageRect.height
    });

  };

  const getRelative = (e) => {

    if (imageLayout.width <= 0 || imageLayout.height <= 0) {
      return { x: 0, y: 0 };
    }

    if (!imageRef.current) {
      return { x: 0, y: 0 };
    }

    const imageRect = imageRef.current.getBoundingClientRect();

    const x = ((e.clientX - imageRect.left) / imageRect.width) * 100;
    const y = ((e.clientY - imageRect.top) / imageRect.height) * 100;

    return { x, y };

  };

  const normalize = (g) => {

    let { x, y, width, height } = g;

    if (width < 0) {
      x += width;
      width = Math.abs(width);
    }

    if (height < 0) {
      y += height;
      height = Math.abs(height);
    }

    return { x, y, width, height };

  };

  const onMouseDown = (e) => {

    if (!isEditable || editing) return;
    if (imageLayout.width <= 0 || imageLayout.height <= 0) return;
    if (e.target.closest("[data-image-annotation='true']")) return;

    const pos = getRelative(e);

    setDrawing({
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0
    });

  };

  const onMouseMove = (e) => {

    if (!drawing) return;

    const pos = getRelative(e);

    setDrawing(prev => ({
      ...prev,
      width: pos.x - prev.x,
      height: pos.y - prev.y
    }));

  };

  const onMouseUp = () => {
    if (!isEditable) return;

    if (!drawing) return;

    const g = normalize(drawing);

    setDrawing(null);

    if (g.width < MIN_SIZE || g.height < MIN_SIZE)
      return;

    setEditing({
      geometry: g,
      data: {
        text: "",
        ...defaultAnnotationData,
      },
      isNew: true
    });

  };

  const save = () => {

    if (!editing?.data?.text?.trim())
      return;

    let next;

    if (editing.isNew) {

      const annotationOrder = Date.now() + Math.random();

      next = [
        ...annotations,
        {
          geometry: editing.geometry,
          data: {
            ...editing.data,
            author: editing.data?.author || currentAuthor,
            id: crypto.randomUUID(),
            order: annotationOrder
          }
        }
      ];

    } else {

      next = annotations.map(a =>
        a.data.id === editing.data.id
          ? {
            ...a,
            data: {
              ...editing.data,
              author: editing.data?.author || a.data?.author || currentAuthor,
            }
          }
          : a
      );

    }

    setEditing(null);
    onChange?.(next);

  };

  const closeEditor = () => {
    setEditing(null);
  };

  const remove = () => {

    const next = annotations.filter(a => a.data.id !== editing.data.id);
    setEditing(null);
    onChange?.(next);

  };

  const startEdit = (ann, e) => {

    e.stopPropagation();

    setEditing({
      geometry: ann.geometry,
      data: { ...ann.data },
      isNew: false
    });

  };

  const getCoords = (g) => {

    if (imageLayout.width <= 0 || imageLayout.height <= 0) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }

    return {
      left: imageLayout.left + ((g.x / 100) * imageLayout.width),
      top: imageLayout.top + ((g.y / 100) * imageLayout.height),
      width: (g.width / 100) * imageLayout.width,
      height: (g.height / 100) * imageLayout.height
    };

  };

  const getPopupPosition = (g) => {

    if (imageLayout.width <= 0 || imageLayout.height <= 0) return DEFAULT_POPUP_POSITION;

    const left = imageLayout.left + ((g.x / 100) * imageLayout.width);
    const top = imageLayout.top + (((g.y + g.height) / 100) * imageLayout.height) + 10;

    const maxLeft = imageLayout.left + imageLayout.width - 280;

    return {
      left: Math.max(10, Math.min(left, maxLeft)),
      top
    };

  };

  useEffect(() => {

    setImageLayout(EMPTY_IMAGE_LAYOUT);
    setDrawing(null);

    updateImageLayout();

    if (!imageRef.current) {
      return undefined;
    }

    const onResize = () => {
      updateImageLayout();
    };

    const onLoad = () => {
      requestAnimationFrame(() => {
        updateImageLayout();
      });
    };

    const imageElement = imageRef.current;

    imageElement.addEventListener("load", onLoad);
    window.addEventListener("resize", onResize);

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateImageLayout();
      });

      resizeObserver.observe(imageElement);
    }

    return () => {
      imageElement.removeEventListener("load", onLoad);
      window.removeEventListener("resize", onResize);
      resizeObserver?.disconnect();
    };

  }, [src]);

  useEffect(() => {

    if (!editing || !containerRef.current) return;

    const popup = containerRef.current.querySelector(".annotation-editor");

    if (popup) {
      popup.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }

  }, [editing]);

  const cursor = editing
    ? "default"
    : isEditable
      ? "crosshair"
      : "default";

  const drawingCoords = drawing ? getCoords(normalize(drawing)) : null;
  const editingPosition = editing ? getPopupPosition(editing.geometry) : null;
  const editingCoords = editing ? getCoords(editing.geometry) : null;

  const editingWidth = 260;
  const editingStyle = editing
    ? {
      top: editingPosition.top,
      left: editingPosition.left + editingWidth > containerRef.current.offsetWidth ? 
        containerRef.current.offsetWidth - editingWidth : 
        editingPosition.left,
      width: editingWidth,
      zIndex: 1000
    }
    : null;

  return (
    <div
      ref={containerRef}
      className="position-relative rounded"
      style={{ cursor, overflow: "visible" }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >

      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className="w-100 d-block rounded"
        style={{ height: "auto", objectFit: "contain" }}
        draggable={false}
        onLoad={updateImageLayout}
      />

      {/* EXISTING ANNOTATIONS */}

      {isLayoutReady && annotations.map((a, index) => {

        const g = getCoords(a.geometry);
        const annotationId = a.data?.id;
        const annotationNumber = annotationNumbers[annotationId] || index + 1;

        return (
          <div
            key={annotationId}
            data-image-annotation="true"
            style={{
              position: "absolute",
              border: "2px solid #f4c542",
              backgroundColor: "rgba(255, 230, 120, 0.35)",
              left: g.left,
              top: g.top,
              width: g.width,
              height: g.height,
              cursor: "pointer"
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => startEdit(a, e)}
          >
            <div
              className="d-flex align-items-center justify-content-center font-weight-bold"
              style={{
                position: "absolute",
                top: "-12px",
                left: "-12px",
                width: "24px",
                height: "24px",
                borderRadius: "999px",
                backgroundColor: "#f4c542",
                color: "#5c4400",
                fontSize: "12px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)"
              }}
            >
              {annotationNumber}
            </div>
          </div>
        );

      })}

      {/* DRAWING RECT */}

      {drawingCoords && (
        <div
          style={{
            position: "absolute",
            border: "2px dashed #f4c542",
            left: drawingCoords.left,
            top: drawingCoords.top,
            width: drawingCoords.width,
            height: drawingCoords.height
          }}
        />
      )}

      {editing?.isNew && editingCoords && (
        <div
          style={{
            position: "absolute",
            border: "2px solid #f4c542",
            backgroundColor: "rgba(255, 230, 120, 0.35)",
            left: editingCoords.left,
            top: editingCoords.top,
            width: editingCoords.width,
            height: editingCoords.height,
            pointerEvents: "none"
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center font-weight-bold"
            style={{
              position: "absolute",
              top: "-12px",
              left: "-12px",
              width: "24px",
              height: "24px",
              borderRadius: "999px",
              backgroundColor: "#f4c542",
              color: "#5c4400",
              fontSize: "12px",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)"
            }}
          >
            +
          </div>
        </div>
      )}

      {/* EDITOR */}
      {editing && editingPosition && (
        <CommentForm
          isNew={editing.isNew}
          isEditable={isEditable}
          text={editing?.data?.text || ""}
          style={editingStyle}
          onMouseDown={(e) => e.stopPropagation()}
          onChangeText={(text) =>
            setEditing((prev) => ({
              ...prev,
              data: {
                ...prev.data,
                text
              }
            }))
          }
          onRemove={remove}
          onClose={closeEditor}
          onSave={save}
        />
      )}

    </div>
  );

};

export default ImageAnnotator;
